import { Tabs, TabsList, TabsPanel, TabsTrigger } from '@amore/ui/components/tabs'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { createContext, type ReactNode, useContext, useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { z } from 'zod'

export const plannerSearchSchema = z.object({
  panel: z.string().optional().catch(undefined),
  supplier: z.string().optional().catch(undefined),
})
export const PlannerEditingContext = createContext(false)
const PlannerPanelContext = createContext('')
const PlannerTabsSurfaceContext = createContext<'card' | 'plain'>('card')

export function PlannerTabs({
  items,
  children,
  surface = 'card',
}: {
  items: [string, string][]
  children: ReactNode
  surface?: 'card' | 'plain'
}) {
  const root = useRef<HTMLDivElement>(null)
  const revealedAttempt = useRef(0)
  const {
    formState: { submitCount, errors },
  } = useFormContext()
  const search = useSearch({ strict: false })
  const navigate = useNavigate()
  const active = items.some(([id]) => id === search.panel)
    ? (search.panel ?? '')
    : (items[0]?.[0] ?? '')
  useEffect(() => {
    if (!search.supplier) return
    const target = Array.from(
      root.current?.querySelectorAll<HTMLElement>('[data-supplier-key]') ?? [],
    ).find((element) => element.dataset.supplierKey === search.supplier)
    if (
      !target ||
      target.closest<HTMLElement>('[data-planner-panel]')?.dataset.plannerPanel !== active
    )
      return
    if (target instanceof HTMLDetailsElement) target.open = true
    target.scrollIntoView?.({ block: 'center' })
  }, [search.supplier, active])
  useEffect(() => {
    if (!submitCount || revealedAttempt.current === submitCount || !Object.keys(errors).length)
      return
    const frame = requestAnimationFrame(() => {
      revealedAttempt.current = submitCount
      const invalid = root.current?.querySelector<HTMLElement>('[aria-invalid="true"]')
      const panel = invalid?.closest<HTMLElement>('[data-planner-panel]')?.dataset.plannerPanel
      let parent = invalid?.parentElement
      while (parent) {
        if (parent instanceof HTMLDetailsElement) parent.open = true
        parent = parent.parentElement
      }
      if (panel && panel !== active) {
        void navigate({ to: '.', search: (prev) => ({ ...prev, panel }), resetScroll: false }).then(
          () => invalid?.focus(),
        )
      } else invalid?.focus()
    })
    return () => cancelAnimationFrame(frame)
  }, [submitCount, errors, active, navigate])
  return (
    <PlannerPanelContext.Provider value={active}>
      <PlannerTabsSurfaceContext.Provider value={surface}>
        <Tabs
          ref={root}
          value={active}
          onValueChange={(panel) => {
            void navigate({ to: '.', search: (prev) => ({ ...prev, panel }), resetScroll: false })
          }}
          className={
            surface === 'card' ? 'rounded-panel border border-border bg-card p-4 sm:p-6' : undefined
          }
        >
          <TabsList aria-label="Části plánování">
            {items.map(([id, label]) => (
              <TabsTrigger key={id} value={id}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
          {children}
        </Tabs>
      </PlannerTabsSurfaceContext.Provider>
    </PlannerPanelContext.Provider>
  )
}
export function PlannerPanel({
  value,
  children,
  disabled: disabledOverride,
}: {
  value: string
  children: ReactNode
  disabled?: boolean
}) {
  const active = useContext(PlannerPanelContext)
  const editingDisabled = useContext(PlannerEditingContext)
  const disabled = disabledOverride ?? editingDisabled
  const surface = useContext(PlannerTabsSurfaceContext)
  return (
    <TabsPanel
      value={value}
      data-planner-panel={value}
      forceMount
      hidden={active !== value}
      className={
        surface === 'card'
          ? 'data-[state=inactive]:hidden [&>fieldset>[data-slot=card]]:border-0 [&>fieldset>[data-slot=card]]:p-0 [&>fieldset>[data-slot=card]]:shadow-none'
          : 'data-[state=inactive]:hidden'
      }
    >
      <fieldset disabled={disabled} className="min-w-0 border-0 p-0">
        {children}
      </fieldset>
    </TabsPanel>
  )
}
